import { useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import CloseIcon from 'assets/images/shopping/closeIcon';
import useLockScroll from 'hooks/useLockScroll';
import FlagImageByUrl from 'components/FlagImageByUrl/FlagImageByUrl';
import NudosStaticAlertInComponent from 'components/NudosComponents/NudosStaticAlertInComponent/NudosStaticAlertInComponent';
import GenericLoader from 'components/GenericLoader/GenericLoader';
import { InformationIcon } from 'assets/images/shopping';
import { copyTextToClipboard } from '../../trackingOrder.utils';
import { ORDER_LISTING_OPERATIONS_ID, TRELLO_COLUMN_CONTENT_CLASS } from '../../trackingOrder.constants';
import usePreparationChecklistDrawer from './usePreparationChecklistDrawer';
import './PreparationChecklistDrawer.scss';

interface IPreparationChecklistDrawer {
  isOpen: boolean;
  onClose: () => void;
  orderId: number;
  shipmentId?: number;
  companyName: string;
  companyFlagUrl?: string;
  onChecklistCountersChange?: (payload: { shipmentId: number; total: number; completed: number }) => void;
}

/**
 * Modal drawer que se desliza desde la derecha mostrando el checklist
 * de preparación de un envío con lista de tareas y productos.
 * @param props - Props del componente.
 */
const PreparationChecklistDrawer = ({
  isOpen,
  onClose,
  orderId,
  shipmentId,
  companyName,
  companyFlagUrl,
  onChecklistCountersChange
}: IPreparationChecklistDrawer) => {
  const {
    checklistItems,
    checkedItems,
    updatingItems,
    isBulkUpdating,
    isLoading,
    hasError,
    hasPendingOperations,
    canBulkUpdate,
    allChecked,
    selectAllRef,
    handleCheckboxChange,
    handleToggleAll,
    refetch
  } = usePreparationChecklistDrawer({ isOpen, shipmentId });
  const additionalElementsToLock = [ORDER_LISTING_OPERATIONS_ID, TRELLO_COLUMN_CONTENT_CLASS];
  useLockScroll(isOpen, additionalElementsToLock);

  const portalContainer = useMemo(() => document.getElementById(ORDER_LISTING_OPERATIONS_ID), []);
  const hasChecklistItems = checklistItems.length > 0;
  const selectAllChecked = canBulkUpdate && allChecked;
  const selectAllDisabled = !canBulkUpdate || isBulkUpdating;

  const handleClose = () => {
    if (hasPendingOperations) return;
    onClose();
  };

  const handleCopyOrderNumber = () => copyTextToClipboard(`N°${orderId}`);

  /**
   * Notifica al componente padre cuando cambian los contadores del checklist
   * para actualizar el número en tiempo real en la card.
   */
  useEffect(() => {
    if (!isOpen || !shipmentId || !onChecklistCountersChange) return;

    const total = checklistItems.length;
    const completed = Object.values(checkedItems).filter(Boolean).length;

    onChecklistCountersChange({ shipmentId, total, completed });
  }, [isOpen, shipmentId, checklistItems, checkedItems, onChecklistCountersChange]);

  if (!isOpen) return null;

  const loadingContent = (
    <div className="loadingState">
      <GenericLoader sizeLoader={60} />
    </div>
  );

  const errorContent = (
    <div className="errorState">
      <span>Error al cargar el checklist</span>
      <button className="retryButton" onClick={refetch}>
        Reintentar
      </button>
    </div>
  );

  const emptyChecklistContent = (
    <div className="checklistEmptyState">
      Este envío no tiene checklist.
    </div>
  );

  const checklistContent = checklistItems.map(({ id, label }) => {
    const isChecked = checkedItems[id] || false;
    const isDisabled = !!updatingItems[id] || isBulkUpdating;

    return (
      <label key={id} className="checklistItem">
        <input
          type="checkbox"
          checked={isChecked}
          onChange={() => handleCheckboxChange(id)}
          className="checklistCheckbox"
          disabled={isDisabled}
        />
        <span className="checklistLabel">{label}</span>
      </label>
    );
  });

  const successContent = (
    <>
      <div className="checklistMeta">
        <div className="metaTopRow">
          <div className="orderInfo">
            <button type="button" className="orderNumber" onClick={handleCopyOrderNumber}>
              N°{orderId}
            </button>
          </div>

          <label className="selectAll">
            <input
              ref={selectAllRef}
              type="checkbox"
              checked={selectAllChecked}
              onChange={handleToggleAll}
              className="selectAllCheckbox"
              disabled={selectAllDisabled}
            />
            <span>Marcar todas</span>
          </label>
        </div>

        <div className="companyRow">
          {companyFlagUrl && (
            <span className="companyFlag" aria-hidden="true">
              <FlagImageByUrl url={companyFlagUrl} className="companyFlagImage" />
            </span>
          )}
          <span className="companyName">{companyName}</span>
        </div>

        <NudosStaticAlertInComponent
          customClassname="preparationChecklistInfoAlert"
          color="blue"
          alertOptions={{
            icon: <InformationIcon className="preparationChecklistInfoIcon" />
          }}
          bodyAlertComponent={
            <span className="preparationChecklistInfoText">
              Los checkboxes marcados son acciones ya realizadas sobre la orden.
            </span>
          }
        />
      </div>

      <div className="checklistSection">
        {hasChecklistItems ? checklistContent : emptyChecklistContent}
      </div>
    </>
  );

  const renderDrawerContent = () => {
    if (isLoading) return loadingContent;
    if (hasError) return errorContent;
    return successContent;
  };

  const drawerContent = (
    <>
      <div className="preparationChecklistDrawerOverlay" onClick={handleClose} />

      <div className="preparationChecklistDrawer">
        <header className="drawerHeader">
          <h2 className="drawerTitle">Checklist de Preparación</h2>
          <button
            className="closeButton"
            onClick={handleClose}
            aria-label="Cerrar modal"
            disabled={hasPendingOperations}
          >
            <CloseIcon />
          </button>
        </header>

        <div className="drawerContent">
          {renderDrawerContent()}
        </div>
      </div>
    </>
  );

  if (portalContainer) {
    return createPortal(drawerContent, portalContainer);
  }

  return drawerContent;
};

export default PreparationChecklistDrawer;

